import React, { useRef, useEffect } from 'react';

// Render an email's HTML inside an isolated iframe so every template renders
// exactly as designed (its own tables/widths/styles) without our app CSS
// interfering — and without the email's CSS leaking into the app. The frame
// auto-sizes to its content height. Shared by the reading pane (EmailViewer)
// and the compose "Original Message" quote block (EmailCompose), since both
// need to show real email HTML safely rather than as raw markup or a
// hand-rolled quote-stripping regex.
export default function EmailBodyFrame({ html }) {
  const ref = useRef(null);

  useEffect(() => {
    const iframe = ref.current;
    if (!iframe) return undefined;
    const doc = iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document);
    if (!doc) return undefined;

    const resize = () => {
      try {
        const h = Math.max(
          doc.body ? doc.body.scrollHeight : 0,
          doc.documentElement ? doc.documentElement.scrollHeight : 0
        );
        if (h) iframe.style.height = `${h + 6}px`;
      } catch { /* cross-origin guard */ }
    };

    doc.open();
    doc.write(
      `<!DOCTYPE html><html><head><meta charset="utf-8">` +
      `<meta name="viewport" content="width=device-width, initial-scale=1">` +
      `<base target="_blank">` +
      `<style>` +
      `html,body{margin:0;padding:0;background:#fff;}` +
      `body{font-family:'Segoe UI',Arial,Helvetica,sans-serif;font-size:14px;color:#242424;` +
      `line-height:1.6;padding:24px 28px;word-wrap:break-word;overflow-wrap:break-word;}` +
      `img{max-width:100%;height:auto;}` +
      `table{max-width:100%;}` +
      `a{color:#0f6cbd;}` +
      `*{box-sizing:border-box;}` +
      `</style></head><body>${html || ''}</body></html>`
    );
    doc.close();

    resize();
    const timers = [setTimeout(resize, 120), setTimeout(resize, 500), setTimeout(resize, 1200)];
    const imgs = doc.images || [];
    for (let i = 0; i < imgs.length; i++) {
      imgs[i].addEventListener('load', resize);
      imgs[i].addEventListener('error', resize);
    }
    return () => { timers.forEach(clearTimeout); };
  }, [html]);

  return (
    <iframe
      ref={ref}
      className="em-viewer-frame"
      title="Email content"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
    />
  );
}
